import React, { useMemo, useRef, useState } from 'react';
import NavigationBar from '../../../components/NavigationBar';
import { makePrioStyles } from '../../../theme/utils';
import { useTranslation } from 'react-i18next';
import { TimeRecord, TimeRecordsFilter } from '../../../models/TimeRecord';
import { Button, Drawer } from '@prio365/prio365-react-library';
import {
  DocumentTemplateFormWrapper,
  DocumentTemplateFormWrapperRef,
} from '../../documents/components/DocumentTemplateFrom/DocumentTemplateFormWrapper';
import LegacyDocumentTemplateFormWrapper, {
  LegacyDocumentTemplateFormWrapperRef,
} from '../../documents/components/DocumentTemplateFrom/LegacyDocumentTemplateFormWrapper';
import { DocumentTemplatePreview } from '../../documents/components/DocumentTemplateFrom/DocumentTemplatePreview';
import { ProjectId } from '../../../models/Types';
import { useDispatch } from 'react-redux';
import { openTimeAndLeaveManagementDrawer } from '../../timeAndLeaveManagement/actions';
import TimeRecordsPage from './TimeRecordsPage';
import FilterContextProvider from '../../../components/Filter/FilterContextProvider';
import DocumentTemplateCategoryContextProvider from '../../documents/components/DocumentTemplateCategoryContextProvider';
import DocumentTemplateTagContextProvider from '../../documents/components/DocumentTemplateTagContextProvider';
import { FilterBar } from '../../../components/Filter/FilterBar';
import moment from 'moment';
import { calculateRelativeDateRange } from '../util';
import {
  abortDriveItemFetch,
  DriveItemsFetchContext,
} from '../../documents/actions';
import useAccessRights2 from '../../users/hooks/useAccessRights2';

const useStyles = makePrioStyles((theme) => ({
  root: {
    flex: 1,
    height: '100%',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  content: {
    width: '100%',
    overflow: 'hidden',
  },
  drawerContent: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface TimeRecordControllingSubModuleProps {
  projectId: ProjectId;
}

export const TimeRecordSubModule: React.FC<
  TimeRecordControllingSubModuleProps
> = (props) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { projectId } = props;
  const { t } = useTranslation();
  const formWrapperRef = useRef<LegacyDocumentTemplateFormWrapperRef>(null);
  const betaFormWrapperRef = useRef<DocumentTemplateFormWrapperRef>(null);
  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------- States / Attributes / Selectors
  const [reportDrawerVisible, setReportDrawerVisible] = useState(false);
  const [betaReportDrawerVisible, setBetaReportDrawerVisible] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [betaThumbnailURL, setBetaThumbnailURL] = useState<String[]>([]);
  const [betaSelectedTemplate, setBetaSelectedTemplate] = useState({});
  const [isBetaFormValid, setIsBetaFormValid] = useState(false);
  const [dateRange, setDateRange] = useState<TimeRecordsFilter>({
    from: null,
    to: null,
  });
  const [timeRecords, setTimeRecords] = useState<TimeRecord[]>([]);

  const {
    checkGlobalRoles,
    checkOfficeRolesOfProjectId,
    checkProjectRolesOfProjectId,
  } = useAccessRights2();

  const isController = useMemo(() => {
    return (
      checkGlobalRoles(['globalController']) ||
      checkOfficeRolesOfProjectId(['officeController'], projectId) ||
      checkProjectRolesOfProjectId(['projectController'], projectId)
    );
  }, [
    projectId,
    checkGlobalRoles,
    checkOfficeRolesOfProjectId,
    checkProjectRolesOfProjectId,
  ]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const onCloseReport = () => {
    setReportDrawerVisible(false);
    dispatch(
      abortDriveItemFetch(DriveItemsFetchContext.Other, undefined, true)
    );
  };

  const onCreateDocumentSuccess = () => {
    setReportDrawerVisible(false);
    dispatch(
      abortDriveItemFetch(DriveItemsFetchContext.Other, undefined, true)
    );
  };
  const onStartCreateDocument = () => {
    setReportDrawerVisible(false);
    dispatch(
      abortDriveItemFetch(DriveItemsFetchContext.Other, undefined, true)
    );
  };

  const onCancelReportDrawer = () => {
    setReportDrawerVisible(false);
    dispatch(
      abortDriveItemFetch(DriveItemsFetchContext.Other, undefined, true)
    );
  };

  const onBetaCreateDocumentSuccess = () => {
    setBetaReportDrawerVisible(false);
    dispatch(
      abortDriveItemFetch(DriveItemsFetchContext.Other, undefined, true)
    );
  };
  const onStartBetaCreateDocument = () => {
    setBetaReportDrawerVisible(false);
    dispatch(
      abortDriveItemFetch(DriveItemsFetchContext.Other, undefined, true)
    );
  };
  const onCancelBetaReportDrawer = () => {
    setBetaReportDrawerVisible(false);
    dispatch(
      abortDriveItemFetch(DriveItemsFetchContext.Other, undefined, true)
    );
    setBetaSelectedTemplate({});
    setBetaThumbnailURL([]);
  };

  const showTimeRecordDrawer = () =>
    dispatch(openTimeAndLeaveManagementDrawer({ tab: 'timeRecords' }));

  const handleSearchStringChange = (searchString: string) => {
    if (searchString !== null) {
      const fromMatch = searchString?.match(/Day\sge\s'([^']+)'/);
      const toMatch = searchString?.match(/Day\sle\s'([^']+)'/);
      const relativeMatch = searchString?.match(/Day\srel\s'([^']+)'/);

      let newDateRange: TimeRecordsFilter = {
        from: null,
        to: null,
      };

      if (fromMatch || toMatch) {
        newDateRange = {
          from: fromMatch ? moment.utc(fromMatch[1]) : null,
          to: toMatch ? moment.utc(toMatch[1]).endOf('day') : null,
        };
      } else if (relativeMatch) {
        const relativeValue = relativeMatch[1];

        const calculatedRange = calculateRelativeDateRange(relativeValue);
        if (calculatedRange) {
          newDateRange.from = calculatedRange.from;
          newDateRange.to = calculatedRange.to;
        }
      } else {
        newDateRange = {
          from: moment.utc('1900-01-01T00:00:00.000Z'),
          to: moment.utc('2200-12-31T23:59:59.999Z'),
        };
      }
      if (
        (newDateRange?.from && !newDateRange.from.isSame(dateRange?.from)) ||
        (newDateRange?.to && !newDateRange.to.isSame(dateRange?.to))
      ) {
        setDateRange(newDateRange);
      }
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  //#endregion

  if (!projectId) return null;

  return (
    <div className={classes.root}>
      <NavigationBar>
        <Button onClick={showTimeRecordDrawer} iconProp={['fal', 'pencil']}>
          <span>{t('common:navigationBar.recordTime')}</span>
        </Button>
      </NavigationBar>
      <TimeRecordsPage
        className={classes.content}
        contextType={isController ? 'project' : 'me'}
        setReportDrawerVisible={setReportDrawerVisible}
        setBetaReportDrawerVisible={setBetaReportDrawerVisible}
        setTimeRecords={setTimeRecords}
        setSearchString={handleSearchStringChange}
      />
      {isController && (
        <>
          <Drawer
            title={t('timeRecords:navigationBar.createReport')}
            closable={true}
            onClose={onCloseReport}
            visible={reportDrawerVisible}
            customWidth={800}
            contentClassName={classes.drawerContent}
            footer={
              <div style={{ textAlign: 'right' }}>
                <Button type="default" onClick={onCloseReport}>
                  {t('common:actions.cancel')}
                </Button>
                <Button
                  onClick={() => formWrapperRef.current?.onFinish()}
                  disabled={!isFormValid}
                  style={{
                    marginLeft: '12px',
                  }}
                >
                  {t('common:actions.save')}
                </Button>
              </div>
            }
          >
            <DocumentTemplateTagContextProvider>
              <DocumentTemplateCategoryContextProvider>
                <FilterContextProvider
                  options={{
                    enabled: false,
                    staleTime: 1000 * 60 * 60 * 24,
                  }}
                  searchType="documentTemplates"
                  customDefaultSearchParameters={[
                    {
                      parameterName: 'IsReport',
                      defaultValue: 'true',
                      defaultMethod: 'eq',
                    },
                  ]}
                  disableURLQuery={true}
                >
                  <FilterBar hiddenPickers={['IsReport']} />
                  <LegacyDocumentTemplateFormWrapper
                    showOnlyReports={true}
                    onSuccess={onCreateDocumentSuccess}
                    onStartCreateDocument={onStartCreateDocument}
                    onCancel={onCancelReportDrawer}
                    ref={formWrapperRef}
                    setIsFormValid={setIsFormValid}
                    timeRecords={timeRecords}
                    filter={dateRange}
                    projectPickerFilter={({
                      isArchived,
                      projectId: _projectId,
                      parentProject,
                    }) => {
                      return (
                        !isArchived &&
                        (_projectId === projectId ||
                          parentProject === projectId)
                      );
                    }}
                  />
                </FilterContextProvider>
              </DocumentTemplateCategoryContextProvider>
            </DocumentTemplateTagContextProvider>
          </Drawer>
          <Drawer
            title={t('timeRecords:navigationBar.createReport')}
            closable={true}
            onClose={onCancelBetaReportDrawer}
            visible={betaReportDrawerVisible}
            customWidth={800}
            contentClassName={classes.drawerContent}
            footer={
              <div style={{ textAlign: 'right' }}>
                <Button type="default" onClick={onCancelBetaReportDrawer}>
                  {t('common:actions.cancel')}
                </Button>
                <Button
                  onClick={() => betaFormWrapperRef.current?.onFinish()}
                  disabled={!isBetaFormValid}
                  style={{
                    marginLeft: '12px',
                  }}
                >
                  {t('common:actions.save')}
                </Button>
              </div>
            }
            mask={
              <DocumentTemplatePreview
                thumbnailURL={betaThumbnailURL}
                selectedTemplate={betaSelectedTemplate}
              />
            }
          >
            <DocumentTemplateTagContextProvider>
              <DocumentTemplateCategoryContextProvider>
                <FilterContextProvider
                  options={{
                    enabled: false,
                    staleTime: 1000 * 60 * 60 * 24,
                  }}
                  searchType="templates"
                  disableURLQuery={true}
                  customDefaultSearchParameters={[
                    {
                      parameterName: 'TemplateType',
                      defaultValue: 'timeRecord',
                      defaultMethod: 'eq',
                    },
                    {
                      parameterName: 'AuthType',
                      defaultValue: 'controller',
                      defaultMethod: 'eq',
                    },
                    {
                      parameterName: 'IsArchived',
                      defaultValue: 'false',
                      defaultMethod: 'eq',
                    },
                  ]}
                >
                  <DocumentTemplateFormWrapper
                    showOnlyReports={true}
                    onSuccess={onBetaCreateDocumentSuccess}
                    onStartCreateDocument={onStartBetaCreateDocument}
                    onCancel={onCancelBetaReportDrawer}
                    onThumbnailURLChange={setBetaThumbnailURL}
                    onSelectedTemplateChange={setBetaSelectedTemplate}
                    ref={betaFormWrapperRef}
                    setIsFormValid={setIsBetaFormValid}
                    projectPickerFilter={({
                      isArchived,
                      projectId: _projectId,
                      parentProject,
                    }) => {
                      return (
                        !isArchived &&
                        (_projectId === projectId ||
                          parentProject === projectId)
                      );
                    }}
                  />
                </FilterContextProvider>
              </DocumentTemplateCategoryContextProvider>
            </DocumentTemplateTagContextProvider>
          </Drawer>
        </>
      )}
    </div>
  );
};
export default TimeRecordSubModule;
